<template>
	<div class="purchases">
		<LoadingSpinner
			v-if="!profile || isProcessing"
			class="purchases__loading-spinner"
		/>
		<template v-else>
			<ModalConfirm
				modal-name="confirmCancelSubscription"
				:title="$I18n.trans('arena.fan_account.purchases.subscription_cancel.title')"
				:text="$I18n.trans('arena.fan_account.purchases.subscription_cancel.text')"
				:confirm-button-text="$I18n.trans('arena.fan_account.purchases.subscription_cancel.confirm')"
			/>
			<!-- SUBSCRIPTIONS -->
			<div class="purchases__section">
				<SectionTitle
					:title="$I18n.trans('arena.fan_account.purchases.subscriptions.title')"
					type="md"
				/>
				<div
					v-if="subscriptions.length === 0"
					class="purchases__box"
				>
					{{ $I18n.trans('arena.fan_account.purchases.subscriptions.empty', { mail: profile.email }) }}
				</div>

				<div
					v-for="(subscription, i) in subscriptions"
					:key="i"
				>
					<SectionTitle
						v-if="firstArchivedSubscriptionIndex > 0 && i === firstArchivedSubscriptionIndex"
						:title="$I18n.trans('commons.archive')"
						type="sm"
					/>
					<div
						class="purchases__box"
						:class="{'is-inactive': !subscription.isInactive}"
					>
						<div
							class="purchases__box-main"
						>
							<img
								v-if="subscription.offer.imageUrl"
								class="purchases__box-image"
								:src="$Helpers.getScaledImageUrl(subscription.offer.imageUrl, 80, { heightIsWidth: true })"
							>
							<div class="purchases__box-text-w">
								<h4 class="purchases__box-title">
									{{ $Helpers.getTranslatedContent(subscription.name) }}
								</h4>
								<div
									class="purchases__box-status"
									:class="'-status-' + subscription.subscriptionState"
								>
									<div
										v-if="subscription.refunded"
									>
										{{ $I18n.trans('arena.fan_account.purchases.refunded') }}
									</div>
									<div
										v-else-if="subscription.isActive"
									>
										{{ $I18n.trans('arena.fan_account.purchases.active') }} | {{ $I18n.trans('arena.fan_account.purchases.renewal_at', { date: getDateTime(moment(subscription.renewalAt).unix(), { withTime: false }) }) }}
									</div>
									<div
										v-else-if="subscription.isCancelled"
									>
										{{ $I18n.trans('arena.fan_account.purchases.cancelled') }} | {{ $I18n.trans('arena.fan_account.purchases.valid_until', { date: getDateTime(moment(subscription.expiresAt).unix(), { withTime: false }) }) }}
									</div>
									<div
										v-else
									>
										{{ $I18n.trans('arena.fan_account.purchases.inactive') }}<br>
									</div>
								</div>
							</div>
							<div class="purchases__box-right">
								<V2Button
									v-if="subscription.isActive"
									:text="$Helpers.ucfirst($I18n.trans('arena.fan_account.purchases.cancel'))"
									modifier="danger"
									:theme="!$arenaConfig.isLightTheme ? 'dark' : null"
									@click="cancelSubscription(subscription)"
								/>
								<V2Button
									v-if="subscription.isCancelled || subscription.isInactive"
									disabled
									:text="$Helpers.ucfirst($I18n.trans('arena.fan_account.purchases.cancelled'))"
									modifier="tertiary"
									:theme="!$arenaConfig.isLightTheme ? 'dark' : null"
								/>
							</div>
						</div>
						<div
							v-if="subscription.paymentType === 'payment'"
							class="purchases__box-billing"
						>
							<a
								:href="laroute.route('arena.payment.invoice', { paymentOrder: subscription.id, user: profile.id })"
								class="purchases__box-billing-link"
								target="_blank"
							>
								{{ $I18n.trans('arena.fan_account.purchases.invoice') }} <Icon
									class="purchases__box-billing-icon"
									icon-name="pdf"
									size="sm"
								/>
							</a>
							<div class="purchases__box-billing-right">
								{{ formattedPrice(subscription) }} / {{ $Helpers.ucfirst($I18n.trans('payments.validity.' + subscription.validity)) }}
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- PACKAGES -->
			<div class="purchases__section">
				<SectionTitle
					:title="$I18n.trans('arena.fan_account.purchases.packages.title')"
					type="md"
				/>
				<div
					v-if="packages.length === 0"
					class="purchases__box"
				>
					{{ $I18n.trans('arena.fan_account.purchases.packages.empty', { mail: profile.email }) }}
				</div>

				<div
					v-for="(eventPackage, i) in packages"
					:key="i"
				>
					<SectionTitle
						v-if="firstArchivedPackageIndex > 0 && i === firstArchivedPackageIndex"
						:title="$I18n.trans('commons.archive')"
						type="sm"
					/>
					<div
						class="purchases__box"
						:class="{'is-inactive': !eventPackage.isInactive}"
					>
						<div
							class="purchases__box-main"
						>
							<img
								v-if="eventPackage.offer.imageUrl"
								class="purchases__box-image"
								:src="$Helpers.getScaledImageUrl(eventPackage.offer.imageUrl, 80, { heightIsWidth: true })"
							>
							<div class="purchases__box-text-w">
								<h4 class="purchases__box-title">
									{{ $Helpers.getTranslatedContent(eventPackage.name) }}
								</h4>
								<div
									class="purchases__box-status"
									:class="'-status-' + eventPackage.subscriptionState"
								>
									<div
										v-if="eventPackage.refunded"
									>
										{{ $I18n.trans('arena.fan_account.purchases.refunded') }}
									</div>
									<div
										v-else-if="eventPackage.isActive"
									>
										{{ $I18n.trans('arena.fan_account.purchases.active') }}
									</div>
									<div
										v-else
									>
										{{ $I18n.trans('arena.fan_account.purchases.inactive') }}
									</div>
								</div>
							</div>
						</div>
						<div
							v-if="eventPackage.paymentType === 'payment'"
							class="purchases__box-billing"
						>
							<a
								:href="laroute.route('arena.payment.invoice', { paymentOrder: eventPackage.id, user: profile.id })"
								class="purchases__box-billing-link"
								target="_blank"
							>
								{{ $I18n.trans('arena.fan_account.purchases.invoice') }} <Icon
									class="purchases__box-billing-icon"
									icon-name="pdf"
									size="sm"
								/>
							</a>
							<div class="purchases__box-billing-right">
								<span
									v-if="eventPackage.paymentType === 'voucher'"
									class="purchases__voucher"
								>
									{{ $I18n.trans('arena.fan_account.purchases.with_voucher') }}
								</span>
								<span v-else>
									{{ formattedPrice(eventPackage) }}
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- EVENTS -->
			<div class="purchases__section">
				<SectionTitle
					:title="$I18n.trans('arena.fan_account.purchases.events.title')"
					type="md"
				/>
				<div
					v-if="events.length === 0"
					class="purchases__box"
				>
					{{ $I18n.trans('arena.fan_account.purchases.events.empty') }}
				</div>
				<table
					v-else
					class="purchases__table"
				>
					<tr>
						<td class="purchases__table-el -header">
							{{ $I18n.trans_choice('commons.event', 1) }}
						</td>
						<td class="purchases__table-el -header">
							{{ $I18n.trans('arena.fan_account.purchases.events.table_labels.amount') }}
						</td>
						<td class="purchases__table-el -header">
							{{ $I18n.trans('arena.fan_account.purchases.events.table_labels.pruchase_date') }}
						</td>
						<td class="purchases__table-el -header u-show-as-table-cell@md">
							{{ $I18n.trans('arena.fan_account.purchases.events.table_labels.order_code') }}
						</td>
						<td class="purchases__table-el -header u-show-as-table-cell@md">
							{{ $I18n.trans('arena.fan_account.purchases.invoice') }}
						</td>
					</tr>
					<tr
						v-for="(event, i) in events"
						:key="i"
						class="purchases__table-row"
						:class="{'was-refunded': event.refunded}"
					>
						<td class="purchases__table-el -body">
							<a
								v-if="!event.eventDeleted"
								class="purchases__table-link"
								:href="event.link"
							>
								{{ event.eventName }}<br>
								{{ event.eventDate }}
								<Icon
									class="purchases__table-link-icon"
									icon-name="launch"
									size="xs"
								/>
							</a>
							<span v-else>
								{{ event.eventName }}<br>
								{{ event.eventDate }}
							</span>
						</td>
						<td
							class="purchases__table-el -body"
							:class="event.refunded ? '-attention' : ''"
						>
							<span
								v-if="event.paymentType === 'voucher'"
								class="purchases__voucher"
							>
								{{ $I18n.trans('arena.fan_account.purchases.with_voucher') }}
							</span>
							<span
								v-else-if="event.refunded"
							>
								<span class="purchases__box-strikethrough">{{ event.price }}</span> {{ $I18n.trans('arena.fan_account.purchases.refunded') }}
							</span>
							<span v-else>
								{{ event.price }}
							</span>
						</td>
						<td class="purchases__table-el -body">
							{{ event.orderDate }}
						</td>
						<td class="purchases__table-el -body u-show-as-table-cell@md">
							{{ event.orderCode }}
						</td>
						<td class="purchases__table-el -body u-show-as-table-cell@md">
							<a
								v-if="event.paymentType === 'payment'"
								:href="laroute.route('arena.payment.invoice', { paymentOrder: event.orderId, user: profile.id })"
								class="purchases__box-billing-link"
								target="_blank"
							>
								{{ $I18n.trans('arena.fan_account.purchases.invoice') }} <Icon
									class="purchases__box-billing-icon"
									icon-name="pdf"
									size="sm"
								/>
							</a>
						</td>
					</tr>
				</table>
			</div>
		</template>
	</div>
</template>

<script>
import SectionTitle from '@js/components/SectionTitle';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import { actions } from '@shared/js/store/modules/user';
import { mapState, mapActions } from 'vuex';
import { getDateTime } from '@shared/utils/timeUtils';
import { formatCurrency } from '@shared/utils/formatNumber';
import { OrderStatus } from '@shared/enums/OrderStatus';
import V2Button from '@shared/components/Button';
import Icon from '@shared/components/Icon';
import ModalConfirm from '@shared/components/ModalConfirm';
import { httpClient } from '@shared/js/api/httpClient';
import laroute from '@laroute';

export default {
	name:       'Purchases',
	components: { SectionTitle, LoadingSpinner, V2Button, ModalConfirm, Icon },
	data() {
		return {
			isProcessing: false,
			navItems:     [
				{
					label:  $I18n.trans('arena.fan_account.purchases.ppv'),
					url:    'ppv',
					active: true,
				},
				{
					label:  $I18n.trans_choice('arena.fan_account.purchases.package', 1),
					url:    'packages',
					active: false,
				},
				{
					label:  $I18n.trans_choice('arena.fan_account.purchases.subscription', 2),
					url:    'subscriptions',
					active: false,
				},
			],
			getDateTime,
			moment,
			laroute,
		};
	},
	computed: {
		...mapState({
			profile: state => state.user.profile,
		}),
		subscriptions() {
			return (this.profile?.allResources?.subscriptions || [])
				.map(this.subscriptionStateMap)
				.sort((a, b) => a.isActive && !b.isActive ? -1 : 1);
		},
		packages() {
			return (this.profile?.allResources?.packages || [])
				.map(this.subscriptionStateMap)
				.sort((a, b) => a.isActive && !b.isActive ? -1 : 1);
		},
		events() {
			return (this.profile?.allResources?.events || [])
				.map(event => {
					return {
						orderId:      event.id,
						eventName:    event.name,
						eventDate:    getDateTime(new Date(event.start).getTime() / 1000, { withTime: false }),
						orderDate:    getDateTime(new Date(event.createdAt).getTime() / 1000, { withTime: false }),
						price:        formatCurrency(event.currencyCode, event.amount),
						refunded:     event.status === OrderStatus.REFUNDED,
						orderCode:    event.code,
						eventDeleted: event.eventDeleted,
						paymentType:  event.paymentType,
						link:         event.url,
					};
				}).sort((a, b) => a.eventDate > b.eventDate ? -1 : 1);
		},
		firstArchivedSubscriptionIndex() {
			return this.subscriptions.findIndex(subscription => subscription.isInactive);
		},
		firstArchivedPackageIndex() {
			return this.packages.findIndex(pkg => pkg.isInactive);
		},
	},
	methods: {
		...mapActions({
			updateProfile: `user/${actions.LOAD_PROFILE}`,
		}),
		subscriptionStateMap(subscription) {
			subscription.subscriptionState = subscription.isActive ? 'active' : subscription.isCancelled ? 'cancelled' : subscription.refunded ? 'refunded' : 'inactive';
			return subscription;
		},
		async cancelSubscription(subscription) {
			window.eventBus.$emit('modal:confirmCancelSubscription');
			const result = await this.handleSubscriptionCancelConfirmation();
			if (result) {
				try {
					this.isProcessing = true;
					await httpClient.cancelSubscription(subscription.id, this.profile.id);

					this.$toast.success($I18n.trans('arena.fan_account.purchases.subscription_cancel.success'));
				} catch (_e) {
					this.$toast.error(this.$I18n.trans('arena.fan_account.purchases.subscription_cancel.error'));
				} finally {
					this.isProcessing = false;
					this.updateProfile({ forceReload: true });
				}
			}
		},
		handleSubscriptionCancelConfirmation() {
			return new Promise((resolve) => {
				window.eventBus.$on('confirmationMessage:confirmCancelSubscription', (confirmed) => {
					resolve(confirmed);
				});
			});
		},
		formattedPrice(offer) {
			return formatCurrency(offer.currencyCode, offer.amount);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.purchases {
	position: relative;
}

.purchases__section {
	margin-bottom: $sp6;
}

.purchases__loading-spinner {
	position: absolute;
	right: 50%;
	top: 10vh;
	transform: translateX(-50%);
}

.purchases__box {
	flex-direction: row;
	gap: $sp4;
	padding: $sp4;
	border: 1px solid var(--color-grey-800);
	border-radius: $border-radius;
	background-color: var(--color-grey-900);
	margin-bottom: $sp5;
}

.purchases__box-main {
	display: flex;
	align-items: flex-start;
}

.purchases__box-image {
	width: 40px;
	height: auto;
	margin-right: $sp4;
}

.purchases__box-text-w {
	.purchases__box.is-inactive & {
		color: var(--color-grey-500);
	}
}

.purchases__box-title {
	@include font-size(fs-130);
	margin-bottom: $sp3;
}

.purchases__table {
	width: 100%;
	border-collapse: collapse;
}

.purchases__table-row {
	position: relative;

	&:nth-child(odd) {
		background-color: var(--color-grey-950);
	}

	&:hover {
		background-color: var(--color-grey-900);
	}
}

.purchases__table-el {
	padding: $sp3;
	border-bottom: 1px solid var(--color-grey-900);

	&.-header {
		@include font(primary, medium, normal, fs-80);
		color: var(--color-grey-300);
	}

	&.-body {
		@include font(primary, book, normal, fs-80);
		vertical-align: middle;
	}

	&.-attention {
		color: var(--color-dimmed-orange);
	}

	@include size(sm-up) {
		padding: $sp4;
	}
}

.purchases__voucher {
	padding: 5px 10px;
	display: inline-block;
	background-color: var(--color-hype-yellow--inactive);
	color: var(--text-on-hype-yellow);
}

.purchases__box-right {
	margin-left: auto;
}

.purchases__box-status {
	&.-status-active {
		color: var(--color-success);
	}

	&.-status-cancelled {
		color: var(--color-red);
	}

	&.-status-refunded {
		color: var(--color-dimmed-orange);
	}

	&.-status-inactive {
		color: var(--color-grey-600);
	}
}

.purchases__box-billing {
	display: flex;
	color: var(--color-grey-400);
	margin-top: $sp4;
	display: flex;
	align-items: center;

	.-has-image & {
		margin-left: 56px;
	}
}

.purchases__box-billing-right {
	margin-left: auto;
}

.purchases__box-billing-icon {
	margin-left: $sp2;
}

.purchases__box-billing-link {
	display: flex;
	align-items: center;
}

.purchases__box-strikethrough {
	text-decoration: line-through;
}
</style>
